import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  FaRocket,
  FaGlobeAmericas,
  FaApple,
  FaMedal,
} from "react-icons/fa";

/* ─── data ─── */

const stats = [
  {
    icon: <FaRocket />,
    value: 20,
    suffix: "+",
    label: "Proyectos Entregados",
    color: "#00c8ff",
  },
  {
    icon: <FaGlobeAmericas />,
    value: 5,
    suffix: "",
    label: "Países con Clientes",
    color: "#7c3aed",
  },
  {
    icon: <FaApple />,
    value: 1,
    suffix: "er",
    label: "Chileno en Apple WWDC",
    color: "#10b981",
  },
  {
    icon: <FaMedal />,
    value: 100,
    suffix: "",
    label: "Jóvenes Líderes 2024",
    color: "#f59e0b",
  },
];

/* ─── hook: animated counter ─── */

const useCountUp = (target, duration = 2000, shouldStart = false) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!shouldStart) return;
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, shouldStart]);

  return count;
};

/* ─── styled ─── */

const Section = styled.section`
  padding: 72px 0;
  background: #0a0c1a;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(0, 200, 255, 0.04) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 40px;

  @media (max-width: 900px) {
    padding: 0 20px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Card = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "accentColor",
})`
  text-align: center;
  padding: 32px 20px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: ${({ accentColor }) => accentColor}25;
    transform: translateY(-4px);
  }
`;

const IconCircle = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "accentColor",
})`
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: ${({ accentColor }) => accentColor}12;
  border: 1px solid ${({ accentColor }) => accentColor}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 20px;
  color: ${({ accentColor }) => accentColor};
`;

const StatValue = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "accentColor",
})`
  font-size: 2.8em;
  font-weight: 900;
  color: #ffffff;
  line-height: 1;
  margin-bottom: 8px;
  letter-spacing: -1px;

  span {
    font-size: 0.6em;
    color: ${({ accentColor }) => accentColor};
    font-weight: 700;
  }

  @media (max-width: 600px) {
    font-size: 2.4em;
  }
`;

const StatLabel = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const Divider = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "accentColor",
})`
  width: 32px;
  height: 2px;
  background: ${({ accentColor }) => accentColor}40;
  margin: 12px auto 14px;
  border-radius: 2px;
`;

/* ─── counter item ─── */

const CounterCard = ({ stat, isVisible }) => {
  const count = useCountUp(stat.value, 2000, isVisible);

  return (
    <Card accentColor={stat.color}>
      <IconCircle accentColor={stat.color}>{stat.icon}</IconCircle>
      <StatValue accentColor={stat.color}>
        {count}
        <span>{stat.suffix}</span>
      </StatValue>
      <Divider accentColor={stat.color} />
      <StatLabel>{stat.label}</StatLabel>
    </Card>
  );
};

/* ─── component ─── */

export const Impact = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Section ref={ref}>
      <Inner>
        <Grid>
          {stats.map((s, i) => (
            <CounterCard key={i} stat={s} isVisible={isVisible} />
          ))}
        </Grid>
      </Inner>
    </Section>
  );
};
